import { classifyAvailability } from '../utils'
import { apiFetch } from '../api/apiFetch'

/**
 * Sloty modułów: kryteria -> konkretna, najtańsza oferta.
 *
 * Wyciągnięte z ekranu preferencji, bo tego samego rozwiązywania potrzebuje
 * asystent doboru przy trybie „TYLKO wybrane moduły". Dwie kopie tej logiki
 * rozjechałyby się przy pierwszej zmianie kryteriów, a użytkownik zobaczyłby
 * w ustawieniach inny moduł niż ten, który asystent wstawia do zestawu.
 *
 * Slot trzyma KRYTERIA, nie model: „Bifacial do 400 zł od 500 W". Wynik zmienia
 * się z każdym scrapem — zapis konkretnego modelu byłby nieaktualny po tygodniu.
 */

const apiUrl = process.env.REACT_APP_API_URL || '/api/'

const getJson = async (path) => {
  try {
    const res = await apiFetch(`${apiUrl}${path}`)
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

// ── Dostępność oferty w slocie ───────────────────────────────────────────────
// Liczba => porównanie z progiem użytkownika. Tekst => klasyfikacja słownikowa
// z utils.classifyAvailability: przepuszczamy tylko tony „high" i „mid" —
// „Mało", „Na wyczerpaniu", „Na zamówienie" i brak danych odpadają, bo wycena
// firmowa na module, którego zaraz nie będzie, kończy się telefonem „jednak nie mamy".

export const sztukiZTekstu = (av) => {
  if (av === null || av === undefined) return null
  const t = String(av).replace(/\s+/g, ' ').trim()
    .replace(/\s*(szt\.?|sztuki?|sztuk|opak\.?|opakowa\w*|pcs)\.?$/i, '').trim()
  return /^\d+$/.test(t) ? parseInt(t, 10) : null
}

export const dostepnoscOk = (av, minSztuk) => {
  const n = sztukiZTekstu(av)
  if (n !== null) return n >= minSztuk
  return ['high', 'mid'].includes(classifyAvailability(av).tone)
}

export const PUSTY_SLOT = {
  max_price: '', power: '', types: [],
  min_product_warranty: '', min_efficiency: '', max_length: '', frame_colour: '',
}

/** Slot bez żadnego kryterium — świeżo dodany, zanim użytkownik cokolwiek wpisze. */
export const slotPusty = (slot) => (
  !slot.max_price && !slot.power && !(slot.types || []).length
  && !slot.min_product_warranty && !slot.min_efficiency
  && !slot.max_length && !slot.frame_colour
)

/** Najtańsza oferta spełniająca slot; null gdy nic nie przechodzi. */
export const szukajModulu = async (slot, producenci, sklepy, minSztuk) => {
  const q = new URLSearchParams({ order_by_price: 'asc', available_only: 'true', limit: '60' })
  if (slot.max_price) q.set('max_price', slot.max_price)
  // Moc DOKŁADNA (min=max): przy ograniczonej mocy przyłączeniowej 505 W
  // zamiast 500 W to nie „lepiej", tylko „nie mieści się".
  if (slot.power) { q.set('min_power', slot.power); q.set('max_power', slot.power) }
  if (slot.min_product_warranty) q.set('min_product_warranty', slot.min_product_warranty)
  if (slot.min_efficiency) q.set('min_efficiency', slot.min_efficiency)
  if (slot.max_length) q.set('module_length_max', slot.max_length)
  if (slot.frame_colour) q.set('frame_colour', slot.frame_colour)
  ;(slot.types || []).forEach((t) => q.append('types', t))
  producenci.forEach((m) => q.append('manufacturers', m))
  sklepy.forEach((s) => q.append('sellers', s))
  const d = await getJson(`panels/?${q.toString()}`)
  const items = d?.items || []
  return items.find((o) => o.price > 0 && dostepnoscOk(o.availability, minSztuk)) || null
}

/**
 * Identyfikatory modeli, do których rozwiązują się sloty — lista dozwolonych
 * modułów dla trybu „TYLKO wybrane moduły" w asystencie.
 *
 * Puste sloty POMIJAMY: slot bez kryteriów pokazuje w ustawieniach produkt
 * polecany przez VoltValue, a to podpowiedź, nie wybór użytkownika. Wpuszczenie
 * jej do twardego filtra znaczyłoby, że firma wycenia na czymś, czego nie wskazała.
 *
 * Wynik bywa PUSTY i to jest poprawna odpowiedź („nic nie spełnia kryteriów"),
 * a nie powód, żeby wrócić do doboru bez ograniczeń.
 */
export const rozwiazSlotyPelne = async (sloty, producenci, sklepy, minSztuk) => {
  const doSprawdzenia = (sloty || []).filter((s) => s && !slotPusty(s))
  if (doSprawdzenia.length === 0) return []
  const wyniki = await Promise.all(
    doSprawdzenia.map((s) => szukajModulu(s, producenci || [], sklepy || [], minSztuk || 0)),
  )
  // Deduplikacja po `model_id`: dwa różne sloty potrafią rozwiązać się do tego
  // samego modelu (np. „Bifacial do 400 zł" i „od 500 W" trafiają w ten sam
  // produkt) — w asystencie pokazałby się wtedy dwa razy na jednej liście.
  const widziane = new Set()
  return wyniki.filter((o) => {
    if (!o || typeof o.model_id !== 'number' || widziane.has(o.model_id)) return false
    widziane.add(o.model_id)
    return true
  })
}

export const rozwiazSloty = async (sloty, producenci, sklepy, minSztuk) => {
  const oferty = await rozwiazSlotyPelne(sloty, producenci, sklepy, minSztuk)
  return oferty.map((o) => o.model_id)
}
